import { SERVICE_FEE } from '@/constants/platform'

export interface DateParts {
    weekday: string
    month: string
    day: string
    year: string
    time: string
    zone: string
}

const safeZone = (timeZone: string): string => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date(0))
        return timeZone
    }
    catch {
        return 'UTC'
    }
}

export const getDateParts = (iso: string, timeZone: string): DateParts => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: safeZone(timeZone),
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
    }).formatToParts(new Date(iso))

    const get = (type: string): string => parts.find(part => part.type === type)?.value ?? ''

    return {
        weekday: get('weekday'),
        month: get('month'),
        day: get('day'),
        year: get('year'),
        time: `${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`,
        zone: get('timeZoneName')
    }
}

export const formatEventDate = (iso: string, timeZone: string): string => {
    const p = getDateParts(iso, timeZone)
    return `${p.weekday}, ${p.month} ${p.day}, ${p.year}`
}

export const formatEventTime = (iso: string, timeZone: string): string => {
    const p = getDateParts(iso, timeZone)
    return p.time
}

export const formatShortDate = (iso: string, timeZone: string): string => {
    const p = getDateParts(iso, timeZone)
    return `${p.weekday}, ${p.month} ${p.day}`
}

export const formatDateTime = (iso: string, timeZone: string): string => {
    const p = getDateParts(iso, timeZone)
    return `${p.month} ${p.day}, ${p.year}, ${p.time}`
}

export const formatEventRange = (startIso: string, endIso: string, timeZone: string): string => {
    const start = getDateParts(startIso, timeZone)
    const end = getDateParts(endIso, timeZone)
    const sameDay = start.year === end.year && start.month === end.month && start.day === end.day
    const hoursApart = (new Date(endIso).getTime() - new Date(startIso).getTime()) / 3600000

    if (sameDay || hoursApart <= 12) {
        return `${start.weekday}, ${start.month} ${start.day}, ${start.year} · ${start.time} to ${end.time} ${end.zone}`
    }

    return `${start.weekday}, ${start.month} ${start.day} ${start.time} to ${end.weekday}, ${end.month} ${end.day} ${end.time} ${end.zone}`
}

const pad = (value: number): string => String(value).padStart(2, '0')

const zonedFields = (date: Date, timeZone: string) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: safeZone(timeZone),
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).formatToParts(date)

    const get = (type: string): number => Number(parts.find(part => part.type === type)?.value ?? 0)

    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour: get('hour') % 24,
        minute: get('minute'),
        second: get('second')
    }
}

const offsetMs = (date: Date, timeZone: string): number => {
    const f = zonedFields(date, timeZone)
    const asUtc = Date.UTC(f.year, f.month - 1, f.day, f.hour, f.minute, f.second)
    const truncated = Math.floor(date.getTime() / 1000) * 1000
    return asUtc - truncated
}

export const toZonedInputValue = (iso: string | null | undefined, timeZone: string): string => {
    if (!iso) return ''
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ''
    const f = zonedFields(date, timeZone)
    return `${f.year}-${pad(f.month)}-${pad(f.day)}T${pad(f.hour)}:${pad(f.minute)}`
}

export const zonedInputToIso = (value: string, timeZone: string): string | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value)
    if (!match) return null
    const [, y, m, d, h, min] = match.map(Number)
    const guess = Date.UTC(y, m - 1, d, h, min)
    const first = offsetMs(new Date(guess), timeZone)
    let utc = guess - first
    const second = offsetMs(new Date(utc), timeZone)
    if (second !== first) utc = guess - second
    return new Date(utc).toISOString()
}

export const getDateKey = (iso: string, timeZone: string): string => {
    const f = zonedFields(new Date(iso), timeZone)
    return `${f.year}-${pad(f.month)}-${pad(f.day)}`
}

export const formatMoney = (cents: number, currency: string): string => {
    const code = (currency || 'usd').toUpperCase()
    const whole = cents % 100 === 0

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: code,
            minimumFractionDigits: whole ? 0 : 2,
            maximumFractionDigits: 2
        }).format(cents / 100)
    }
    catch {
        return `${(cents / 100).toFixed(2)} ${code}`
    }
}

export const formatPrice = (cents: number, currency: string): string => {
    return cents === 0 ? 'Free' : formatMoney(cents, currency)
}

export const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US').format(value)
}

export const formatTicketCode = (code: string): string => {
    return code.replace(/(.{4})(?=.)/g, '$1-')
}

export const normalizeTicketCode = (input: string): string => {
    return input.toUpperCase().replace(/^TURNOUT:/, '').replace(/[^A-Z0-9]/g, '')
}

export const pluralize = (count: number, singular: string, plural?: string): string => {
    return count === 1 ? singular : (plural ?? `${singular}s`)
}

export const calculateServiceFee = (discountedSubtotal: number, paidTicketCount: number): number => {
    if (discountedSubtotal <= 0 || paidTicketCount <= 0) return 0
    return Math.round(discountedSubtotal * SERVICE_FEE.percent / 100) + SERVICE_FEE.fixedPerTicket * paidTicketCount
}

export const serviceFeeLabel = (): string => {
    const fixed = SERVICE_FEE.fixedPerTicket < 100
        ? `${SERVICE_FEE.fixedPerTicket}¢`
        : formatMoney(SERVICE_FEE.fixedPerTicket, 'usd')

    return SERVICE_FEE.fixedPerTicket > 0 ? `${SERVICE_FEE.percent}% + ${fixed}` : `${SERVICE_FEE.percent}%`
}

export const calculateDiscount =(subtotal: number, type: string, value: number): number => {
    if (subtotal <= 0) return 0
    if (type === 'percent') return Math.min(subtotal, Math.round(subtotal * Math.min(Math.max(value, 0), 100) / 100))
    return Math.min(subtotal, Math.max(value, 0))
}

export const initials = (name: string): string => {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word[0]?.toUpperCase() ?? '')
        .join('')
}

export const cms = (table: string, id: string, column: string): string => {
    return `${table}:${id}:${column}`
}
